import { releaseRecordingLease, renewRecordingLease } from "./api/recordingLease";
import { newLeaseHolder } from "./leaseHolder";
import { reportRecordingLeaseFailure } from "./recordingLeaseReport";

/**
 * EN-1196: visits this tab has learned were merged into another visit, with the kept visit's id. Fed by
 * the recording lease's 409 merged_visit (recordingLease.ts) and by mergedIntoVisit below; read by the
 * page-level notice so a page loaded before the merge stops presenting the visit as live.
 */
// learnedAt: when this tab learned it, so a merge-state answer fetched later can overrule it (an unmerge).
const mergedInto = new Map<string, { keptVisitId: string; learnedAt: number }>();
const listeners = new Set<() => void>();
let version = 0;
const changed = () => {
    version += 1;
    listeners.forEach((l) => l());
};

export function noteVisitMergedAway(visitId: string, keptVisitId: string) {
    if (mergedInto.get(visitId)?.keptVisitId === keptVisitId) return;
    mergedInto.set(visitId, { keptVisitId, learnedAt: Date.now() });
    changed();
}

/** The visit is not merged away (any more): an unmerge, or a lease the server granted again. */
export function forgetVisitMergedAway(visitId: string) {
    if (mergedInto.delete(visitId)) changed();
}

export const getMergedAwayKeptVisit = (visitId: string | null | undefined) =>
    visitId ? (mergedInto.get(visitId)?.keptVisitId ?? null) : null;
export const getMergedAwayLearnedAt = (visitId: string | null | undefined) =>
    visitId ? (mergedInto.get(visitId)?.learnedAt ?? null) : null;
/** Changes whenever a visit is noted, for useSyncExternalStore. */
export const getMergedAwayVersion = () => version;
export const subscribeMergedAwayVisits = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Asks the server whether the visit was merged away, using the lease endpoint (it answers 409 merged_visit
 * with the kept visit, setting no lease). On a visit that was not merged it does set a lease, so that is
 * released straight away. Uses its own holder, so the probe's release never ends a recorder's lease
 * (EN-1448). Never throws: null when not merged or when the answer is unknown.
 */
export async function mergedIntoVisit(visitId: string): Promise<string | null> {
    const holder = newLeaseHolder();
    try {
        const merged = await renewRecordingLease(visitId, holder);
        if (merged) {
            noteVisitMergedAway(visitId, merged.keptVisitId);
            return merged.keptVisitId;
        }
        void releaseRecordingLease(visitId, holder).catch((error: unknown) =>
            reportRecordingLeaseFailure(error, "release"),
        );
        return null;
    } catch (error) {
        reportRecordingLeaseFailure(error, "renew");
        // The answer may have been lost after the lease was set: release it too, never leave one behind.
        void releaseRecordingLease(visitId, holder).catch((e: unknown) => reportRecordingLeaseFailure(e, "release"));
        return null;
    }
}

/** Tests only. */
export const resetMergedAwayVisitsForTest = () => {
    mergedInto.clear();
    listeners.clear();
    version = 0;
};
